(() => {
  if (customElements.get('typ-product-page')) return;

  class TypProductPage extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.variants = JSON.parse(this.querySelector('[data-variants-json]')?.textContent || '[]');
      this.currency = this.dataset.currency || 'USD';
      this.gallery = this.querySelector('[data-typ-pdp-gallery]');
      this.productData = window.TypologyProductData?.[this.dataset.productHandle] || {};

      this.hydrateContentMedia();
      this.applyShadeColors();
      this.applyProductData();
      this.enhanceContentAccordions();
      this.bindFaqPagination(options);

      this.querySelectorAll('[data-option-input]').forEach((input) => {
        input.addEventListener('change', () => this.selectVariant(), options);
      });
      this.querySelectorAll('[data-gallery-prev],[data-gallery-next]').forEach((button) => {
        button.addEventListener('click', () => {
          this.gallery?.scrollBy({
            left: (button.hasAttribute('data-gallery-prev') ? -1 : 1) * this.gallery.clientWidth,
            behavior: 'smooth'
          });
        }, options);
      });
      this.querySelectorAll('[data-gallery-dot]').forEach((dot) => {
        dot.addEventListener('click', () => {
          this.gallery?.scrollTo({ left: Number(dot.dataset.galleryDot) * this.gallery.clientWidth, behavior: 'smooth' });
        }, options);
      });
      this.gallery?.addEventListener('scroll', () => this.updateCount(), { passive: true, signal: this.abort.signal });
      this.selectVariant(false);
      this.updateCount();
    }

    hydrateContentMedia() {
      this.querySelectorAll('[data-typ-media-key]').forEach((slot) => {
        const key = slot.dataset.typMediaKey;
        const template = [...this.querySelectorAll('[data-typ-media-template]')]
          .find((item) => item.dataset.typMediaTemplate === key);
        if (template?.content) slot.replaceChildren(template.content.cloneNode(true));
      });
    }

    enhanceContentAccordions() {
      this.querySelectorAll('.typology-content-items,.typology-bundle-detail-items').forEach((container) => {
        const items = [...container.querySelectorAll(':scope > .typology-content-item')];
        items.forEach((item, index) => { item.open = index === 0; });
      });
    }

    bindFaqPagination(options) {
      this.querySelectorAll('.typology-faq-section').forEach((section) => {
        const pages = [...section.querySelectorAll('[data-faq-page]')];
        section.querySelectorAll('[data-faq-page-button]').forEach((button) => {
          button.addEventListener('click', () => {
            const selected = button.dataset.faqPageButton;
            pages.forEach((page) => { page.hidden = page.dataset.faqPage !== selected; });
            section.querySelectorAll('[data-faq-page-button]').forEach((candidate) => {
              const active = candidate === button;
              candidate.classList.toggle('is-active', active);
              if (active) candidate.setAttribute('aria-current', 'page');
              else candidate.removeAttribute('aria-current');
            });
          }, options);
        });
      });
    }

    applyProductData() {
      const data = this.productData;
      const setText = (selector, value) => {
        const node = this.querySelector(selector);
        if (node && value) node.textContent = value;
      };
      setText('[data-product-title]', data.title);
      setText('[data-product-code]', data.code);
      setText('[data-product-subtitle]', data.subtitle);
      setText('[data-product-number]', data.number);
      const description = this.querySelector('[data-product-description]');
      if (description) description.innerHTML = data.description || '';
      const sticker = this.querySelector('[data-product-sticker]');
      if (sticker && data.sticker) {
        sticker.textContent = data.sticker;
        sticker.hidden = false;
      }
    }

    applyShadeColors() {
      const map = window.TypologyShadeMap?.[this.dataset.productHandle] || {};
      this.querySelectorAll('[data-shade-value]').forEach((element) => {
        const key = element.dataset.shadeValue.trim().toLowerCase().replace(/\s+/g, ' ');
        if (map[key]) element.style.setProperty('--swatch-color', map[key]);
      });
    }

    selectedOptions() {
      return [...this.querySelectorAll('[data-option-group]')]
        .map((group) => group.querySelector('[data-option-input]:checked')?.value || '');
    }

    selectVariant(updateUrl = true) {
      this.querySelectorAll('[data-option-group]').forEach((group) => {
        const label = group.querySelector('[data-option-selected]');
        const input = group.querySelector('[data-option-input]:checked');
        if (label && input) label.textContent = input.value;
      });
      const chosen = this.selectedOptions();
      const currentId = Number(this.querySelector('[name=id]')?.value);
      const variant = chosen.length
        ? this.variants.find((item) => item.options.every((value, index) => value === chosen[index]))
        : (this.variants.find((item) => item.id === currentId) || this.variants[0]);
      const idInput = this.querySelector('[name=id]');
      const button = this.querySelector('[type=submit]');
      if (!variant) {
        if (button) { button.disabled = true; button.textContent = 'UNAVAILABLE'; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      const capacity = this.querySelector('[data-product-capacity]');
      const metadata = this.productData?.variants?.[variant.sku];
      if (capacity) capacity.textContent = metadata?.capacity || '';
      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
      }
      const price = this.querySelector('[data-price-current]');
      const compare = this.querySelector('[data-price-compare]');
      const sale = this.querySelector('[data-sale-badge]');
      if (price) price.textContent = this.money(variant.price);
      if (compare) {
        compare.textContent = variant.compare_at_price > variant.price ? this.money(variant.compare_at_price) : '';
        compare.hidden = !(variant.compare_at_price > variant.price);
      }
      if (sale) {
        const isSale = variant.compare_at_price > variant.price;
        sale.hidden = !isSale;
        sale.textContent = isSale ? `-${Math.round((1 - variant.price / variant.compare_at_price) * 100)}%` : '';
      }
      if (variant.featured_media?.id) {
        this.querySelector(`[data-media-id="${variant.featured_media.id}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
      if (updateUrl && history.replaceState) history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
    }

    money(cents) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency })
        .format((Number(cents) || 0) / 100);
    }

    updateCount() {
      if (!this.gallery) return;
      const media = [...this.gallery.children];
      const index = Math.round(this.gallery.scrollLeft / Math.max(1, this.gallery.clientWidth));
      const count = this.querySelector('[data-gallery-count]');
      if (count) count.textContent = `${Math.min(index + 1, media.length)} / ${media.length}`;
      this.querySelectorAll('[data-gallery-dot]').forEach((dot, dotIndex) => {
        const active = dotIndex === Math.min(index, media.length - 1);
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    disconnectedCallback() { this.abort?.abort(); }
  }

  customElements.define('typ-product-page', TypProductPage);
})();
